import type { Post } from '@mg/shared';

/**
 * How All Posts orders its posts, and the names and dates it orders them by. Each sorted Status tab
 * opens in its own order (see {@link defaultOrder}), in the tile layout as well as the list
 * (PostsView), and its list can then be re-sorted by its own columns (PostsTable). Both read these,
 * so the two layouts, the table and its column sorts can never disagree on what "newest", "nearest
 * to today" or a post's name is.
 */

/** The name a post is listed under: its title, or the placeholder its row shows when it has none. */
export function postListName(post: Post): string {
  return post.title || 'Untitled post';
}

/**
 * Names compare the way people read a list: case and accents don't separate two names, and a run of
 * digits compares as a number, so "post 2" comes before "Post 10".
 */
const NAME_COLLATOR = new Intl.Collator(undefined, { sensitivity: 'base', numeric: true });

/** A to Z, the way people read a list. Post names and campaign names compare alike. */
export function compareNames(a: string, b: string): number {
  return NAME_COLLATOR.compare(a, b);
}

/** A to Z by the name each row shows. */
export function compareListNames(a: Post, b: Post): number {
  return compareNames(postListName(a), postListName(b));
}

/**
 * The campaign name a row shows, or null when it shows none (a dash). "No campaign" is not a name
 * that belongs at either end of the alphabet, so those rows sort after every named one, both ways.
 */
export function postCampaignName(post: Post): string | null {
  return post.campaignName || null;
}

/** An ISO time as a number to sort on, or null when there is no usable time. */
function sortTime(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const time = Date.parse(iso);
  return Number.isNaN(time) ? null : time;
}

/**
 * The moment the Published date column shows for a post (it went out, else its schedule), as a
 * number to sort on. Null when there is no usable date; such a row sorts last in both directions.
 */
export function publishedSortTime(post: Post): number | null {
  return sortTime(post.publishedAt ?? post.scheduledAt);
}

/**
 * The moment the Scheduled date column shows for a post (its schedule, else the moment it went out:
 * the same pick as the cell), as a number to sort on. Null when there is no usable date.
 */
export function scheduledSortTime(post: Post): number | null {
  return sortTime(post.scheduledAt ?? post.publishedAt);
}

/** When the post was last changed (the Updated column), as a number to sort on. */
export function updatedSortTime(post: Post): number | null {
  return sortTime(post.updatedAt);
}

/**
 * How far a post's scheduled date lies from `now`, on either side of it: what "nearest to today"
 * sorts on. Null for a post with no date, which counts as the furthest of all ("unscheduled posts are
 * considered furthest") and therefore sorts last.
 */
export function scheduleDistance(post: Post, now: number): number | null {
  const time = scheduledSortTime(post);
  return time == null ? null : Math.abs(time - now);
}

export type SortDir = 'asc' | 'desc';

/**
 * Two sort values in `dir`, a missing one (null) after every present one in BOTH directions: flipping
 * a column must not open it on the rows it knows nothing about, because the top of an ascending list
 * is exactly where a genuine 0, the nearest date or the first name belongs.
 */
export function compareMissingLast<T>(
  a: T | null,
  b: T | null,
  dir: SortDir,
  compare: (x: T, y: T) => number,
): number {
  if (a == null || b == null) return a == null ? (b == null ? 0 : 1) : -1;
  return dir === 'asc' ? compare(a, b) : compare(b, a);
}

const byNumber = (a: number, b: number) => a - b;

/**
 * Posts that tie on a tab's own order fall back to their names and then their ids, so the order
 * never depends on the order the server sent them in (it sends them grouped by campaign).
 */
function byNameThenId(a: Post, b: Post): number {
  return compareListNames(a, b) || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);
}

/** The Published tab's own order: newest first. */
export function newestPublishedFirst(a: Post, b: Post): number {
  return compareMissingLast(publishedSortTime(a), publishedSortTime(b), 'desc', byNumber) || byNameThenId(a, b);
}

/** The Draft tab's own order: the most recently updated first. */
export function newestUpdatedFirst(a: Post, b: Post): number {
  return compareMissingLast(updatedSortTime(a), updatedSortTime(b), 'desc', byNumber) || byNameThenId(a, b);
}

/**
 * The own order of the tabs whose posts wait on a date (In Review, Revise, Scheduled, Failed,
 * Rejected): nearest to today first, on whichever side of today the date falls. Upcoming posts so
 * run soonest to latest, and the missed dates on Failed run most recent to oldest. `now` is read once
 * by the caller for the whole sort, so the clock cannot move between two comparisons.
 */
export function nearestScheduledFirst(now: number): (a: Post, b: Post) => number {
  return (a, b) =>
    compareMissingLast(scheduleDistance(a, now), scheduleDistance(b, now), 'asc', byNumber) ||
    byNameThenId(a, b);
}

/**
 * The kinds of Status tab that sort (PostsView picks one per tab): the Published tab, the Draft tab,
 * and the tabs whose posts wait on a date. Ready to Schedule has none and keeps the API's order.
 */
export type PostsSortProfile = 'published' | 'schedule' | 'drafts';

/** The order a sorted tab opens in, read against one `now`. */
export function defaultOrder(profile: PostsSortProfile, now: number): (a: Post, b: Post) => number {
  if (profile === 'published') return newestPublishedFirst;
  if (profile === 'drafts') return newestUpdatedFirst;
  return nearestScheduledFirst(now);
}
